import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, Landmark, Leaf, Award, ArrowRight } from 'lucide-react';
import Header from './components/Header8';
import Footer from './components/Footer8';

const About8 = () => {
    const navigate = useNavigate();

    const values = [
        { icon: Gem, title: 'Artisanship', desc: 'Every stitch tells a story. Our master artisans bring generations of craft knowledge to each creation, ensuring unparalleled quality and garden-fresh elegance.' },
        { icon: Landmark, title: 'Heritage', desc: 'Rooted in the rich textile traditions of India, we honour time-honoured techniques while reimagining them with a feminine, floral sensibility.' },
        { icon: Leaf, title: 'Sustainability', desc: 'We are committed to ethical sourcing, sustainable materials, and fair-trade practices that respect both artisan communities and the environment.' },
        { icon: Award, title: 'Excellence', desc: 'From concept to creation, we pursue perfection relentlessly. Each piece undergoes rigorous quality assessment before reaching your hands.' },
    ];

    const milestones = [
        { year: '2015', title: 'The First Bloom', desc: 'Founded in Jaipur with a vision to bring garden-inspired elegance to modern Indian fashion.' },
        { year: '2017', title: 'First Collection', desc: 'Launched our debut "Rose Garden" collection, earning acclaim for its unique blend of tradition and femininity.' },
        { year: '2019', title: 'Digital Bloom', desc: 'Launched our online boutique, bringing MEHAK to women across India and beyond.' },
        { year: '2021', title: 'Bridal Line', desc: 'Introduced our exclusive bridal collection, designing dream lehengas for brides who want to bloom.' },
        { year: '2023', title: 'Sustainability Pledge', desc: 'Committed to 100% sustainable sourcing and launched our artisan revival programme.' },
        { year: '2026', title: 'National Recognition', desc: 'Honoured with the Indian Fashion Excellence Award for innovation in feminine ethnic wear.' },
    ];

    const stats = [
        { number: '15,000+', label: 'Happy Customers' },
        { number: '10+', label: 'Years of Craft' },
        { number: '500+', label: 'Unique Designs' },
        { number: '100+', label: 'Artisan Partners' },
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@300;400;500;700&display=swap');`}</style>
            <div style={{ fontFamily: "'DM Sans', sans-serif" }} className="min-h-screen bg-white">
                <Header />
                <main>
                    {/* Hero */}
                    <section className="relative w-full min-h-[60vh] lg:min-h-[75vh] overflow-hidden">
                        <img src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Our Story" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(253,242,248,0.6) 0%, rgba(253,242,248,0.4) 50%, #FFFFFF 100%)' }} />
                        <div className="relative z-10 h-full min-h-[60vh] lg:min-h-[75vh] flex items-center justify-center px-4">
                            <div className="text-center max-w-4xl">
                                <p className="text-sm tracking-[0.4em] uppercase mb-6 font-medium" style={{ color: '#BE185D' }}>Our Legacy</p>
                                <h1 style={{ fontFamily: "'Libre Baskerville', serif" }} className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">Our Story</h1>
                                <p className="text-base lg:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: '#6B7280' }}>
                                    Where tradition meets feminine elegance, crafting timeless pieces that celebrate the beauty of every bloom.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Brand Story */}
                    <section className="py-16 lg:py-28 px-4 lg:px-12" style={{ borderBottom: '1px solid #F9A8D4' }}>
                        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            <div>
                                <div className="w-12 h-1 rounded-full mb-6" style={{ backgroundColor: '#BE185D' }} />
                                <h2 style={{ fontFamily: "'Libre Baskerville', serif" }} className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">Born from the Gardens of Jaipur</h2>
                                <p className="leading-relaxed mb-4" style={{ color: '#6B7280' }}>
                                    MEHAK was born from a simple belief: every woman deserves to bloom. Founded in the rose gardens of Jaipur, we draw inspiration from the delicate beauty of flowers, the rich heritage of Indian textiles, and the timeless grace of feminine fashion.
                                </p>
                                <p className="leading-relaxed mb-8" style={{ color: '#6B7280' }}>
                                    Each MEHAK creation is handcrafted by skilled artisans who have inherited centuries of tradition. From Banarasi silks to Chanderi weaves, every fabric tells a story of heritage, woven with love and finished with rose-kissed elegance.
                                </p>
                                <button onClick={() => navigate('/template8/shop')} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-wider transition-all" style={{ backgroundColor: '#BE185D', color: '#FFFFFF' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#9D174D'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#BE185D'; }}
                                >
                                    Explore Collections <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="aspect-[4/5] overflow-hidden rounded-xl">
                                <img src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Artisan at work" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </section>

                    {/* Stats */}
                    <section className="py-16 lg:py-20 px-4" style={{ backgroundColor: '#FDF2F8' }}>
                        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {stats.map((stat, i) => (
                                <div key={i} className="text-center">
                                    <p className="text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: "'Libre Baskerville', serif", color: '#BE185D' }}>{stat.number}</p>
                                    <p className="text-sm" style={{ color: '#6B7280' }}>{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Values */}
                    <section className="py-16 lg:py-24 px-4" style={{ borderBottom: '1px solid #F9A8D4' }}>
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-sm tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#BE185D' }}>Our Values</p>
                                <h2 style={{ fontFamily: "'Libre Baskerville', serif" }} className="text-3xl lg:text-4xl font-bold">What We Stand For</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                {values.map((val, i) => (
                                    <div key={i} className="text-center p-6 rounded-xl" style={{ backgroundColor: '#FDF2F8' }}>
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F9A8D4' }}>
                                            <val.icon className="w-7 h-7" style={{ color: '#BE185D' }} />
                                        </div>
                                        <h3 className="text-base font-bold mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>{val.title}</h3>
                                        <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{val.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Timeline */}
                    <section className="py-16 lg:py-24 px-4">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-sm tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#BE185D' }}>Our Journey</p>
                                <h2 style={{ fontFamily: "'Libre Baskerville', serif" }} className="text-3xl lg:text-4xl font-bold">The Bloom Timeline</h2>
                            </div>
                            <div className="space-y-8">
                                {milestones.map((m, i) => (
                                    <div key={i} className="flex gap-6 items-start">
                                        <div className="flex-shrink-0 w-16 text-right">
                                            <span className="text-lg font-bold" style={{ color: '#BE185D' }}>{m.year}</span>
                                        </div>
                                        <div className="flex flex-col items-center">
                                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#BE185D' }} />
                                            {i < milestones.length - 1 && <div className="w-px flex-1 min-h-[40px]" style={{ backgroundColor: '#F9A8D4' }} />}
                                        </div>
                                        <div className="flex-1 pb-8">
                                            <h3 className="text-base font-bold mb-1">{m.title}</h3>
                                            <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{m.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default About8;
